<template>
  <div class="sub-menu">
    <div
      v-for="(item, index) in menuItems"
      :key="index"
      class="menu-item"
      :class="{ 'has-submenu': item.submenu && item.submenu.length > 0 }"
    >
      <!-- 1階層メニューアイテム（サブメニューなし） -->
      <div
        v-if="!item.submenu || item.submenu.length === 0"
        class="menu-item-content"
        :class="{ active: isActive(item) }"
        @click="handleItemClick(item)"
      >
        <vs-icon v-if="item.icon" :icon="item.icon" class="menu-icon" />
        <span class="menu-label">{{ item.label }}</span>
      </div>

      <!-- 2階層メニューアイテム（サブメニューあり） -->
      <div v-else>
        <div
          class="menu-item-content menu-item-header"
          :class="{ active: isSubmenuActive(item) }"
          @click="toggleSubmenu(index)"
        >
          <vs-icon v-if="item.icon" :icon="item.icon" class="menu-icon" />
          <span class="menu-label">{{ item.label }}</span>
          <vs-icon
            :icon="
              openSubmenus[index]
                ? 'keyboard_arrow_down'
                : 'keyboard_arrow_right'
            "
            class="menu-arrow"
          />
        </div>
        <transition name="slide-down">
          <div v-show="openSubmenus[index]" class="submenu-items">
            <div
              v-for="(subItem, subIndex) in item.submenu"
              :key="subIndex"
              class="submenu-item"
              :class="{ active: isActive(subItem) }"
              @click="handleItemClick(subItem)"
            >
              <vs-icon
                v-if="subItem.icon"
                :icon="subItem.icon"
                class="submenu-icon"
              />
              <span class="submenu-label">{{ subItem.label }}</span>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SubMenu",
  props: {
    /**
     * メニューアイテムの配列
     * @type {Array}
     * @required
     * @example
     * [
     *   { label: 'メニュー1', icon: 'home', name: 'route-name', params: { id: '1' } },
     *   {
     *     label: 'メニュー2',
     *     icon: 'settings',
     *     submenu: [
     *       { label: 'サブメニュー1', icon: 'circle', name: 'sub-route', params: { id: '2' } },
     *       { label: 'サブメニュー2', icon: 'circle', name: 'sub-route-2', params: { id: '3' } }
     *     ]
     *   }
     * ]
     */
    menuItems: {
      type: Array,
      required: true,
      default: () => [],
    },
    /**
     * アクティブなメニューアイテムのname
     * @type {String}
     * @optional
     */
    activeName: {
      type: String,
      default: "",
    },
    /**
     * アクティブなメニューアイテムのparams
     * @type {Object}
     * @optional
     */
    activeParams: {
      type: Object,
      default: () => ({}),
    },
    /**
     * アクティブなメニューアイテムのクエリパラメーター（オプション＆category専用）
     * @type {Object}
     * @optional
     */
    activeQueries: {
      type: Object,
      default: () => ({}),
    },
    /**
     * 編集中かどうか
     * @type {Boolean}
     * @default false
     */
    isEditing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      openSubmenus: {},
    };
  },
  mounted() {
    this.menuItems.forEach((item, index) => {
      if (!item.submenu || item.submenu.length === 0) return;

      const hasActive = item.submenu.some(subItem => this.isActive(subItem));

      if (hasActive) {
        this.$set(this.openSubmenus, index, true);
      }
    });
  },
  methods: {
    toggleSubmenu(index) {
      this.$set(this.openSubmenus, index, !this.openSubmenus[index]);
    },
    isActive(item) {
      if (!item.name) return false;

      // activeNameとactiveParamsが指定されている場合
      if (this.activeName) {
        if (item.name !== this.activeName) return false;
        // --- params の比較 ---
        if (item.params && Object.keys(item.params).length > 0) {
          if (!this.isParamsMatch(item.params, this.activeParams)) {
            return false;
          }
        }
        // --- query の比較 ---
        if (item.query && Object.keys(item.query).length > 0) {
          if (!this.isQueryMatch(item.query, this.activeQueries)) {
            return false;
          }
        }
        return true;
      }

      // 現在のルートと比較
      if (this.$route) {
        if (this.$route.name !== item.name) return false;
        // paramsが指定されている場合は比較
        if (item.params && Object.keys(item.params).length > 0) {
          return this.isQueriesMatch(item.params, this.$route.params);
        }
        return true;
      }

      return false;
    },
    isParamsMatch(itemParams, routeParams) {
      if (!itemParams || !routeParams) return false;
      return Object.keys(itemParams).every(
        key => itemParams[key] === routeParams[key]
      );
    },
    isQueryMatch(itemParams, routeQuery) {
      if (!itemParams || !routeQuery) return false;
      return Object.keys(itemParams).every(
        key => itemParams[key] === routeQuery[key]
      );
    },
    isSubmenuActive(item) {
      if (!item.submenu) return false;
      return item.submenu.some(subItem => this.isActive(subItem));
    },
    async handleItemClick(item) {
      // 編集中で、現在のルートと異なるルートに遷移しようとする場合
      const shouldShow = this.shouldShowConfirmDialog(item);
      if (this.isEditing && shouldShow) {
        const confirmed = await this.showConfirmDialog();
        if (!confirmed) {
          return; // キャンセルされた場合は遷移しない
        }
      }

      if (item.name) {
        const routeConfig = {
          name: item.name,
        };

        if (item.params) {
          routeConfig.params = item.params;
        }

        if (item.query) {
          routeConfig.query = item.query;
        }

        this.$router.push(routeConfig);
      } else if (item.url) {
        // 後方互換性のため、urlもサポート
        if (item.url.startsWith("http")) {
          window.open(item.url, item.target || "_self");
        } else {
          this.$router.push(item.url);
        }
      }

      if (item.onClick && typeof item.onClick === "function") {
        item.onClick(item);
      }
      this.$emit("item-click", item);
    },
    /**
     * 確認ダイアログを表示すべきかどうか
     */
    shouldShowConfirmDialog(item) {
      if (!item.name) return false;

      // this.$route が存在しない場合は false を返す
      if (!this.$route || !this.$route.name) {
        return false;
      }

      // 現在のルートと異なるルートに遷移しようとする場合
      if (this.$route.name !== item.name) {
        return true;
      }

      // 同じルートでも params が異なる場合（例: 別の船員ID）
      if (item.params && Object.keys(item.params).length > 0) {
        if (!this.isParamsMatch(item.params, this.$route.params)) {
          return true;
        }
      }

      // 同じルートでも query が異なる場合（例: 別の保有資格カテゴリ）
      if (item.query && Object.keys(item.query).length > 0) {
        if (!this.isQueryMatch(item.query, this.$route.query)) {
          return true;
        }
      }

      return false;
    },
    /**
     * 確認ダイアログを表示
     */
    showConfirmDialog() {
      return new Promise(resolve => {
        this.$vs.dialog({
          type: "confirm",
          color: "danger",
          title: "ページを離れますか？",
          text:
            "画面を移動すると、入力中の内容が消えてしまいます。よろしいですか？",
          acceptText: "はい",
          cancelText: "キャンセル",
          accept: () => {
            resolve(true);
          },
          cancel: () => {
            resolve(false);
          },
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

.submenu-items {
  padding: 0 !important;
}

.sub-menu {
  display: flex;
  flex-direction: column;
  width: 100%;

  .menu-item {
    width: 100%;

    &.has-submenu {
      .menu-item-header {
        cursor: pointer;
        user-select: none;
      }
    }

    .menu-item-content {
      display: flex;
      align-items: center;
      padding: 1.2rem 1rem;
      cursor: pointer;
      transition: background-color 0.3s ease;

      &:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }

      &.active {
        color: #2d9cdb;
        font-weight: 600;
      }

      .menu-icon {
        margin-right: 0.75rem;
        font-size: 18px;
      }

      .menu-label {
        flex: 1;
        font-size: 14px;
        line-height: 1.5;
      }

      .menu-arrow {
        margin-left: auto;
        font-size: 18px;
        transition: transform 0.3s ease;
      }
    }

    .submenu-items {
      padding-left: 1rem;
      margin-bottom: 0.5rem;

      .submenu-item {
        display: flex;
        align-items: center;
        padding: 0.5rem 1rem;
        padding-left: 2.5rem;
        cursor: pointer;
        transition: background-color 0.3s ease;

        &:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }

        &.active {
          background-color: rgba(45, 156, 219, 0.1);
          font-weight: 600;
        }

        .submenu-icon {
          margin-right: 0.75rem;
          font-size: 16px;
        }

        .submenu-label {
          flex: 1;
          font-size: 14px;
          line-height: 1.5;
        }
      }
    }
  }
}

// アニメーション
.slide-down-enter-active {
  transition: all 0.6s ease;
  overflow: hidden;
}
.slide-down-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.slide-down-enter,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-top: 0;
  margin-bottom: 0;
}

.slide-down-enter-to,
.slide-down-leave {
  max-height: 500px;
  opacity: 1;
}
</style>
